import Ajv from 'ajv';
import addFormats from 'ajv-formats';
import type { Edge, Node } from '@xyflow/react';
import { schemaToCoarse, type Coarse, type TypeExpr, ANY_T, arrayOf, T_STRING, T_NUMBER, T_BOOLEAN, T_OBJECT } from '@features/flows/utils/types';
import type { NodeBindings } from '@features/flows/utils/types';
import { flattenProperties, stripArraySuffix, type FlatField } from '@features/flows/lib/autocast/schema-utils';
import { isPinned } from './bindings';
import { scanVariablesInObject } from './liquid/runtime';

type Channel = 'exec' | 'data';
type DataOutPin = { id: string; type?: Coarse; label?: string; typeExpr?: TypeExpr };
type Pins = {
  exec?: { in?: { id: string }[]; out?: { id: string }[] };
  data?: {
    in?: { id: string; type?: Coarse; label?: string; required?: boolean }[];
    out?: DataOutPin[];
  };
};

const DEFAULT_EXEC: Required<NonNullable<Pins['exec']>> = {
  in: [{ id: 'in' }],
  out: [{ id: 'out' }],
};
const DEFAULT_DATA_OUT: DataOutPin[] = [{ id: 'data_out' }];

function clonePorts<T extends { id: string }>(ps?: T[]): T[] | undefined {
  return ps ? ps.map((p) => ({ ...(p as any) })) : undefined;
}

export function uniqueIds(ids: Iterable<string>) {
  return Array.from(new Set(ids)).sort();
}

/** Build a stable list of data:in handles from bindings + attrs template vars + existing pins. */
type RJSFSchemaLike = { type?: string; properties?: Record<string, any>; required?: string[] } | any;

export function computeDataInHandles(opts: {
  attrs?: unknown;
  bindings?: NodeBindings;
  existing?: Array<{ id: string; type?: Coarse; label?: string; required?: boolean }> | undefined;
  /** JSON Schema for attrs (if available) – used to infer coarse types for pinned fields */
  attrSchema?: RJSFSchemaLike;
  /** Template-declared data:in ports to always include and use as base typing */
  baseDataIn?: Array<{ id: string; type?: Coarse }> | undefined;
}): Array<{ id: string; type?: Coarse; label?: string; required?: boolean }> {
  const inferred = scanVariablesInObject(opts.attrs);
  const pinned = new Set<string>();
  for (const [field, spec] of Object.entries(opts.bindings ?? {})) {
    if (isPinned(spec) && spec.pin) pinned.add(spec.pin);
  }
  const existingArr = (opts.existing ?? []) as Array<{ id: string; [k: string]: any }>;
  const existingById = new Map(existingArr.map((p) => [p.id, p]));
  // Base ports from template
  const base = Array.isArray(opts.baseDataIn) ? opts.baseDataIn : [];
  const baseIds = base.map((b) => b.id);
  // Build per-field coarse types from attr schema (if present)
  const coarseByField = buildCoarseByField(opts.attrSchema);
  const isReq = buildRequiredSet(opts.attrSchema);
  // Target ids = base (always, preserve order) + dynamic extras (sorted)
  const baseSet = new Set(baseIds);
  const extras = uniqueIds([...Array.from(pinned), ...inferred].filter((id) => !baseSet.has(id)));
  const targetIds = [...baseIds, ...extras];
  const typeFromBase = new Map<string, Coarse>(base.map((b) => [b.id, (b.type as Coarse) ?? ('any' as Coarse)]));
  return targetIds.map((id) => {
    const fromExisting = existingById.get(id);
    if (fromExisting) return { ...(fromExisting as any) };
    const type = typeFromBase.get(id) ?? coarseByField.get(id) ?? ('any' as Coarse);
    const required = isReq.has(id) || undefined;
    return { id, type, required } as any;
  });
}

type SchemaValidity = { signature: string | null; valid: boolean };
const schemaValidityCache = new WeakMap<object, SchemaValidity>();

function computeSchemaSignature(schema: object): string | null {
  try {
    return JSON.stringify(schema, (_key, value) => (typeof value === 'bigint' ? value.toString() : value));
  } catch {
    return null;
  }
}

/** Detect whether the node should derive its data outs from a JSON Schema. */
export function shouldSchemaDrivePins(nodeData: any): boolean {
  const key = String(nodeData?.templateKey ?? nodeData?.handler ?? '');
  if (key !== 'validators.jsonschema') return false;
  const schema = nodeData?.attrs?.schema;
  if (!schema || typeof schema !== 'object') return false;

  const schemaObj = schema as object;
  const signature = computeSchemaSignature(schemaObj);
  const cached = schemaValidityCache.get(schemaObj);
  if (cached && cached.signature === signature) {
    return cached.valid;
  }

  try {
    const ajv = getAjv();
    ajv.compile(schema as any);
    if (signature !== null) {
      schemaValidityCache.set(schemaObj, { signature, valid: true });
    }
    return true;
  } catch {
    if (signature !== null) {
      schemaValidityCache.set(schemaObj, { signature, valid: false });
    }
    return false;
  }
}

function buildSchemaDerivedOutputs(nodeData: any): DataOutPin[] {
  const attrs = nodeData?.attrs ?? {};
  if (attrs?.explodeOutputs !== true) return [];
  const schema = attrs?.schema;
  if (!schema || typeof schema !== 'object') return [];
  let flat: ReturnType<typeof flattenProperties> = [];
  try {
    flat = flattenProperties(schema, { maxFields: 256 });
  } catch {
    return [];
  }
  const extras: DataOutPin[] = [];
  const seen = new Set<string>();
  for (const field of flat) {
    if (!field?.path) continue;
    const stripped = stripArraySuffix(field.path);
    if (!stripped) continue;
    const id = `value.${stripped}`;
    if (seen.has(id)) continue;
    seen.add(id);
    const label = stripped.split('.').pop() ?? stripped;
    const type = coarseFromSchemaField(field.type, field.isArray);
    const typeExpr = fieldToTypeExpr(field);
    extras.push({ id, type, label, typeExpr });
  }
  extras.sort((a, b) => a.id.localeCompare(b.id));
  return extras;
}

function mergeOutPins(base: DataOutPin[], extras: DataOutPin[]): DataOutPin[] {
  const out = base.slice();
  const seen = new Set(out.map((p) => p.id));
  for (const extra of extras) {
    if (!extra.id || seen.has(extra.id)) continue;
    seen.add(extra.id);
    out.push(extra);
  }
  return out;
}

function coarseFromSchemaField(type?: string, isArray?: boolean): Coarse {
  if (isArray) return 'array';
  switch (type) {
    case 'string':
    case 'number':
    case 'boolean':
    case 'object':
    case 'array':
      return type;
    case 'integer':
      return 'number';
    case 'tuple':
      return 'array';
    case undefined:
    case null:
      return 'any';
    default:
      return 'any';
  }
}

/** Compute full pins object for a node's data. */
export function computePinsForNodeData(
  nodeData: any,
  opts?: {
    preserveExistingDataIn?: boolean;
    attrSchema?: RJSFSchemaLike;
    baseDataIn?: Array<{ id: string; type?: Coarse }>;
    baseDataOut?: Array<{ id: string; type?: Coarse }>;
    /** When true, keep previously persisted data.out pins even if schema-driven. */
    preserveExistingDataOut?: boolean;
  },
): Pins {
  const existing: Pins | undefined = nodeData?.pins;
  const attrs = nodeData?.attrs;
  const bindings: NodeBindings | undefined = nodeData?.bindings;
  const schemaDriven = shouldSchemaDrivePins(nodeData);

  const buildTemplateDataOut = (): DataOutPin[] =>
    (opts?.baseDataOut ? clonePorts(opts.baseDataOut) : undefined) ?? clonePorts(DEFAULT_DATA_OUT)!;

  const existingDataOut = clonePorts(existing?.data?.out);

  const dataOut: DataOutPin[] = (() => {
    const preserveOut = opts?.preserveExistingDataOut ?? true;

    if (schemaDriven && !preserveOut) {
      const base = buildTemplateDataOut();
      const extras = buildSchemaDerivedOutputs(nodeData);
      return mergeOutPins(base, extras);
    }

    const base: DataOutPin[] = existingDataOut ?? buildTemplateDataOut();
    if (schemaDriven) {
      const extras = buildSchemaDerivedOutputs(nodeData);
      return mergeOutPins(base, extras);
    }
    return base;
  })();

  return {
    exec: existing?.exec
      ? { in: clonePorts(existing.exec.in)!, out: clonePorts(existing.exec.out)! }
      : { in: clonePorts(DEFAULT_EXEC.in)!, out: clonePorts(DEFAULT_EXEC.out)! },
    data: {
      in: computeDataInHandles({
        attrs,
        bindings,
        existing: opts?.preserveExistingDataIn ? existing?.data?.in : undefined,
        attrSchema: opts?.attrSchema,
        baseDataIn: opts?.baseDataIn,
      }),
      out: dataOut,
    },
  };
}

/** Remove edges that reference handles no longer present on the given node. */
export function pruneInvalidEdgesForNode(
  nodeId: string,
  nodes: Node[],
  edges: Edge[],
): { edges: Edge[] } {
  const node = nodes.find((n) => n.id === nodeId);
  if (!node) return { edges };
  const pins = node.data?.pins as Pins | undefined;
  const dataInIds = new Set((pins?.data?.in ?? []).map((p) => p.id));
  const dataOutIds = new Set((pins?.data?.out ?? []).map((p) => p.id));
  const execInIds = new Set((pins?.exec?.in ?? []).map((p) => p.id));
  const execOutIds = new Set((pins?.exec?.out ?? []).map((p) => p.id));

  const keep = (e: Edge) => {
    const chan: Channel | undefined = (e as any).data?.channel ??
      ((e.sourceHandle as string)?.startsWith('data:') || (e.targetHandle as string)?.startsWith('data:') ? 'data' : 'exec');
    if (chan === 'data') {
      if (e.target === nodeId) {
        const raw = (e as any).data?.rawTargetHandle ?? parseRaw(e.targetHandle as any);
        if (!dataInIds.has(raw)) return false;
      }
      if (e.source === nodeId) {
        const raw = (e as any).data?.rawSourceHandle ?? parseRaw(e.sourceHandle as any);
        if (!dataOutIds.has(raw)) return false;
      }
    } else {
      if (e.target === nodeId) {
        const raw = (e as any).data?.rawTargetHandle ?? parseRaw(e.targetHandle as any);
        if (!execInIds.has(raw)) return false;
      }
      if (e.source === nodeId) {
        const raw = (e as any).data?.rawSourceHandle ?? parseRaw(e.sourceHandle as any);
        if (!execOutIds.has(raw)) return false;
      }
    }
    return true;
  };

  return { edges: edges.filter(keep) };
}

function parseRaw(h?: string | null): string {
  if (!h) return 'in';
  const parts = String(h).split(':');
  return parts.length >= 3 ? parts[2]! : parts[0]!;
}

function buildCoarseByField(schema: RJSFSchemaLike | undefined): Map<string, Coarse> {
  const map = new Map<string, Coarse>();
  try {
    const props = (schema && (schema.properties as Record<string, any>)) || {};
    for (const [k, v] of Object.entries(props)) {
      map.set(k, schemaToCoarse(v));
    }
  } catch {}
  return map;
}
function buildRequiredSet(schema: RJSFSchemaLike | undefined): Set<string> {
  try {
    return new Set<string>(Array.isArray(schema?.required) ? (schema!.required as string[]) : []);
  } catch {
    return new Set();
  }
}

function coarseToTypeExpr(coarse: Coarse): TypeExpr {
  switch (coarse) {
    case 'string':
      return T_STRING;
    case 'number':
      return T_NUMBER;
    case 'boolean':
      return T_BOOLEAN;
    case 'object':
      return T_OBJECT;
    case 'array':
      return arrayOf(ANY_T);
    default:
      return ANY_T;
  }
}

function fieldToTypeExpr(field: FlatField): TypeExpr {
  if (field.isArray) {
    const elemCoarse = coarseFromSchemaField(field.type, false);
    const elemExpr = coarseToTypeExpr(elemCoarse);
    return arrayOf(elemExpr);
  }
  const coarse = coarseFromSchemaField(field.type, false);
  return coarseToTypeExpr(coarse);
}
const getAjv = (() => {
  let instance: Ajv | null = null;
  return () => {
    if (!instance) {
      instance = new Ajv({ allErrors: false, allowUnionTypes: true, strict: false });
      addFormats(instance);
    }
    return instance;
  };
})();
